import type { I_Coupon } from '@fe/types/modules/loyaltyProgram/coupon/backend/responses/schemas';

import { useCustomerLoyalty } from '@/context/LoyaltyContext';
import { usePlatformHandlers } from '@/context/PlatformContext';
import { useWidgetConfig } from '@/context/WidgetConfigContext';
import { COLORS } from '@/helpers/constants/theme';
import { useFetchCustomerLoyalty } from '@/hooks/useFetchCustomerLoyalty';
import { useHoverBackground } from '@/hooks/useHoverBackground';
import { useRedemption } from '@/hooks/useRedemption';
import type { T_CouponsInfo } from '@/types/RegisteredCustomer';

interface I_AvailableCouponsProps {
  coupons: T_CouponsInfo;
}

export const AvailableCoupons = ({ coupons }: I_AvailableCouponsProps) => {
  const availableCoupons = coupons.availableCoupons as I_Coupon[];
  const availablePoints = coupons.availablePoints as number;
  const { customerLoyalty } = useCustomerLoyalty();
  const { redeemCoupon } = usePlatformHandlers();
  const { isRedeeming, applyRedemption } = useRedemption();
  const { refetch } = useFetchCustomerLoyalty({ autoFetch: false });
  const { theme } = useWidgetConfig();

  const {
    backgroundColor: primaryButtonBackgroundColor = COLORS.BRAND_PRIMARY,
    textColor: primaryButtonTextColor = COLORS.TEXT_INVERSE,
  } = theme?.primaryButton ?? {};

  const {
    backgroundColor: primaryButtonHoverBackground,
    hoverHandlers: primaryButtonHoverHandlers,
  } = useHoverBackground({
    baseColor: primaryButtonBackgroundColor,
  });

  if (!availableCoupons || availableCoupons.length === 0) {
    return null;
  }

  const handleRedeem = async (coupon: I_Coupon) => {
    const customerId = customerLoyalty?.basicInfo?.enableId;
    if (!redeemCoupon || !customerId) return;

    await applyRedemption(() =>
      redeemCoupon({
        customerId,
        couponId: coupon._id,
      }),
    );

    // Refetch customer loyalty data to update available coupons and points
    await refetch();
  };

  return (
    <div className='space-y-2'>
      <h5 className='!mb-2'>Fixed Rewards</h5>
      {availableCoupons.map((coupon) => {
        const hasEnoughPoints = availablePoints >= coupon.loyaltyPointCost;

        const disableButton = !hasEnoughPoints || isRedeeming;

        return (
          <div
            key={coupon._id}
            className='flex items-center justify-between rounded-lg bg-gray-50 p-3'
          >
            <div>
              <h5 className='font-medium'>{coupon.titleEn}</h5>
              <h6>{coupon.loyaltyPointCost} points</h6>
            </div>
            <button
              {...primaryButtonHoverHandlers}
              onClick={() => handleRedeem(coupon)}
              disabled={disableButton}
              className={`rounded-full px-4 py-1 text-sm transition-colors ${
                disableButton ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
              style={{
                backgroundColor: disableButton
                  ? COLORS.DISABLED_LIGHT
                  : primaryButtonHoverBackground,
                color: disableButton ? COLORS.DISABLED_DARK : primaryButtonTextColor,
              }}
            >
              Redeem
            </button>
          </div>
        );
      })}
    </div>
  );
};
